import React from 'react'
import _ from 'underscore'
import axios from 'axios'
import { CircleCheck, ExternalLink, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import ToggleOptions from '../toggle_options'
import RequiredHint from '../required_hint'
import SearchSubjects from '../search_subjects/subjects/index'
import AddressAutocomplete from '../address/autocomplete'
import DockedPanel from '../docked_panel'
import store from '../../state/store'

const SECTION_CLASSES = 'text-xs font-semibold uppercase tracking-wide text-muted-foreground'

const learnerTypeOptions = [
  {value: 'daughter', name: 'Daughter'},
  {value: 'son', name: 'Son'},
  {value: 'myself', name: 'Me'},
  {value: 'someone_else', name: 'Someone else'},
]

const lessonMediumNames = {
  local: 'Face-to-face',
  online: 'Online'
}

const urgencyOptions = [
  {value: 'asap', name: 'ASAP'},
  {value: 'this_week', name: 'This week'},
  {value: 'this_month', name: 'This month'},
  {value: 'later', name: 'Later'}
]

// For a family who contacted us directly, in place of filling in the website
// enquiry form for them. It asks for what the website enquiry does, so the
// lead converts into an enquiry straight away.
class NewLead extends React.Component {

  constructor(props) {
    super(props)

    this.state = {
      country_code: '',
      enquirer_name: '',
      enquirer_email_address: '',
      enquirer_contact_number: '',
      learner_type: '',
      learner_choice: '',
      learner_name: '',
      customer_match: null,
      subject_category_name: '',
      subject_level_name: '',
      subject_names: [],
      lesson_medium: '',
      address: null,
      flat_unit_estate: '',
      budget: '',
      urgency: ''
    }

    this.matchRequest = 0
    this.lookupMatchesLater = _.debounce(this.lookupMatches, 400)
  }

  componentWillUnmount() {
    this.lookupMatchesLater.cancel()
  }

  // The customer the lead will be added to, found as the lead itself will be:
  // on the email address, then the mobile number read in the chosen country
  lookupMatches = () => {
    const email_address = this.state.enquirer_email_address.trim()
    const contact_number = this.state.country_code ? this.state.enquirer_contact_number.trim() : ''

    if (!email_address && !contact_number) {
      this.onCustomerMatch(null)
      return
    }

    const request = ++this.matchRequest

    axios.get('/api/sales/leads/matches', {
      params: {
        country_code: this.state.country_code,
        enquirer_email_address: email_address,
        enquirer_contact_number: contact_number,
        learner_name: this.state.learner_name
      }
    })
    .then((response) => {
      if (request === this.matchRequest) {
        this.onCustomerMatch(response.data.customer)
      }
    })
    .catch((error) => {
      window.handleError(error)
    })
  }

  onCustomerMatch(customer_match) {
    const learner_ids = customer_match ? _.map(customer_match.learners, (learner) => `${learner.id}`) : []
    let learner_choice = this.state.learner_choice

    // A learner picked from a customer who no longer matches doesn't carry over,
    // and a name already typed in stays as a new learner
    if (learner_choice !== 'new' && !learner_ids.includes(learner_choice)) {
      learner_choice = this.state.learner_name.trim() ? 'new' : ''
    }

    this.setState({customer_match, learner_choice})
  }

  chosenLearner() {
    const customer_match = this.state.customer_match

    if (!customer_match) {
      return null
    }

    return _.find(customer_match.learners, (learner) => `${learner.id}` === this.state.learner_choice) || null
  }

  // With the customer's learners to choose from, the name is only asked for a new learner
  showLearnerName() {
    const customer_match = this.state.customer_match
    const choosing = !!customer_match && customer_match.learners.length > 0

    return !choosing || this.state.learner_choice === 'new'
  }

  renderCustomerMatch() {
    const customer_match = this.state.customer_match

    if (!customer_match) {
      return null
    }

    return (
      <div className="grid gap-1 rounded-md border border-success/50 bg-success/10 p-3 text-sm">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 font-medium">
            <CircleCheck className="size-4 text-success"/>
            Existing customer: {customer_match.name}
          </span>
          <a href={customer_match.link} target="_blank" className="inline-flex items-center gap-1 text-primary no-underline hover:underline">
            Profile <ExternalLink className="size-3"/>
          </a>
        </div>
        <span className="text-muted-foreground">
          {[customer_match.email_address, customer_match.mobile_number].filter(Boolean).join(' · ')}
        </span>
        <span className="text-muted-foreground">
          Matched on their {(customer_match.matched_on === 'email_address') ? 'email address' : 'mobile number'}. The enquiry will be recorded against this customer's profile.
        </span>
        <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
          <span className="text-muted-foreground">If their details haven't changed, use them for the name, email address and mobile number.</span>
          <Button variant="outline" size="sm" onClick={this.onUseCustomerDetails}>Use customer details</Button>
        </div>
      </div>
    )
  }

  onUseCustomerDetails = () => {
    const customer_match = this.state.customer_match

    this.setState({
      enquirer_name: customer_match.name || '',
      enquirer_email_address: customer_match.email_address || '',
      enquirer_contact_number: customer_match.mobile_number || ''
    }, this.lookupMatchesLater)
  }

  renderLearner() {
    const customer_match = this.state.customer_match
    const learners = customer_match ? customer_match.learners : []
    const matching_learner = customer_match ? customer_match.matching_learner : null

    return (
      <React.Fragment>
        {learners.length > 0 ?
          <div className="grid gap-2">
            <Label htmlFor="new_lead_learner">Learner<RequiredHint/></Label>
            <Select value={this.state.learner_choice || undefined} onValueChange={(value) => this.setState({learner_choice: value})}>
              <SelectTrigger id="new_lead_learner" className="w-full">
                <SelectValue placeholder="Select a learner…"/>
              </SelectTrigger>
              <SelectContent>
                {_.map(learners, (learner) => (
                  <SelectItem key={learner.id} value={`${learner.id}`}>{learner.name}</SelectItem>
                ))}
                <SelectItem value="new">New learner</SelectItem>
              </SelectContent>
            </Select>
          </div>
          : null
        }
        {this.showLearnerName() ?
          <div className="grid gap-2">
            <Label htmlFor="new_lead_learner_name">Learner Name<RequiredHint/></Label>
            <Input
              id="new_lead_learner_name"
              value={this.state.learner_name}
              onChange={(e) => this.setState({learner_name: e.target.value}, this.lookupMatchesLater)}
            />
            {matching_learner ?
              <span className="text-sm text-muted-foreground">
                Matches existing learner {matching_learner.name}; handover will link them.
              </span>
              : null
            }
          </div>
          : null
        }
      </React.Fragment>
    )
  }

  country() {
    return _.findWhere(this.props.new_lead.countries, {country_code: this.state.country_code})
  }

  subjectCategories() {
    const country = this.country()
    return country ? (this.props.new_lead.subject_categories[country.subject_country_code] || []) : []
  }

  subjectLevels() {
    const subject_category = _.findWhere(this.subjectCategories(), {name: this.state.subject_category_name})
    return subject_category ? subject_category.subject_levels : []
  }

  lessonMediums() {
    const country = this.country()
    return country ? country.lesson_mediums : []
  }

  // Where the website only offers online lessons there's nothing to choose
  lessonMedium() {
    const lesson_mediums = this.lessonMediums()
    return (lesson_mediums.length === 1) ? lesson_mediums[0] : this.state.lesson_medium
  }

  onClose = () => {
    store.emit('ui:new_lead:close')
  }

  onCountryChange = (value) => {
    this.setState({
      country_code: value,
      subject_category_name: '',
      subject_level_name: '',
      subject_names: [],
      lesson_medium: '',
      address: null,
      budget: ''
    }, this.lookupMatchesLater)
  }

  onSubjectCategoryChange = (value) => {
    this.setState({
      subject_category_name: value,
      subject_level_name: '',
      subject_names: []
    })
  }

  onSubjectSelect = (subject) => {
    if (!this.state.subject_names.includes(subject.name)) {
      this.setState({subject_names: this.state.subject_names.concat([subject.name])})
    }
  }

  onSubjectRemove = (subject_name) => {
    this.setState({subject_names: _.without(this.state.subject_names, subject_name)})
  }

  onLessonMediumChange = (value) => {
    this.setState({lesson_medium: value})

    if (value === 'local' && !this.props.google_map_loaded) {
      store.emit('ui:google_map:load')
    }
  }

  onPlaceChanged = (place) => {
    this.setState({
      address: {
        formatted_address: place.formatted_address,
        address_components: place.address_components,
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng()
      }
    })
  }

  onCreate = () => {
    if (this.props.new_lead.saving) {
      return
    }

    const lesson_medium = this.lessonMedium()
    const learner = (this.state.learner_type === 'myself') ? null : this.chosenLearner()
    const new_learner = (this.state.learner_type !== 'myself') && !learner && this.showLearnerName()

    const params = {
      country_code: this.state.country_code,
      enquirer_name: this.state.enquirer_name,
      enquirer_email_address: this.state.enquirer_email_address,
      enquirer_contact_number: this.state.enquirer_contact_number,
      learner_type: this.state.learner_type,
      learner_id: learner ? learner.id : '',
      learner_name: new_learner ? this.state.learner_name : '',
      subject_category_name: this.state.subject_category_name,
      subject_level_name: this.state.subject_level_name,
      subject_names: this.state.subject_names,
      lesson_medium: lesson_medium,
      budget: this.state.budget,
      urgency: this.state.urgency
    }

    if (lesson_medium === 'local' && this.state.address) {
      _.extend(params, this.state.address, {flat_unit_estate: this.state.flat_unit_estate})
    }

    store.emit('leads:create', params)
  }

  renderLessons() {
    const country = this.country()

    if (!country) {
      return (
        <p className="text-sm text-muted-foreground">Choose the country to see its subjects.</p>
      )
    }

    const subjectCategoryOptions = _.map(this.subjectCategories(), (subject_category) => {
      return {value: subject_category.name, name: subject_category.name}
    })

    const lesson_mediums = this.lessonMediums()

    const lessonMediumOptions = _.map(lesson_mediums, (lesson_medium) => {
      return {value: lesson_medium, name: lessonMediumNames[lesson_medium]}
    })

    return (
      <React.Fragment>
        <div className="grid gap-2">
          <Label>Subject Category<RequiredHint/></Label>
          <ToggleOptions options={subjectCategoryOptions} pressed={this.state.subject_category_name} onClick={this.onSubjectCategoryChange} />
        </div>
        {this.state.subject_category_name ?
          <React.Fragment>
            <div className="grid gap-2">
              <Label htmlFor="new_lead_subject_level">Subject Level<RequiredHint/></Label>
              <Select value={this.state.subject_level_name || undefined} onValueChange={(value) => this.setState({subject_level_name: value})}>
                <SelectTrigger id="new_lead_subject_level" className="w-full">
                  <SelectValue placeholder="Select a level…"/>
                </SelectTrigger>
                <SelectContent>
                  {_.map(this.subjectLevels(), (subject_level) => (
                    <SelectItem key={subject_level.name} value={subject_level.name}>{subject_level.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Subjects<RequiredHint/></Label>
              {this.state.subject_names.length > 0 ?
                <div className="flex flex-wrap gap-1.5">
                  {_.map(this.state.subject_names, (subject_name) => (
                    <Badge key={subject_name} variant="secondary" size="md">
                      {subject_name}
                      <button type="button" className="cursor-pointer" title={`Remove ${subject_name}`} onClick={() => this.onSubjectRemove(subject_name)}>
                        <X className="size-3"/>
                      </button>
                    </Badge>
                  ))}
                </div>
                : null
              }
              <SearchSubjects
                value=""
                country_code={this.state.country_code}
                subject_category_name={this.state.subject_category_name}
                onChange={() => {}}
                onSelect={this.onSubjectSelect}
              />
            </div>
          </React.Fragment>
          : null
        }
        {lesson_mediums.length > 1 ?
          <div className="grid gap-2">
            <Label>Lesson Medium<RequiredHint/></Label>
            <ToggleOptions options={lessonMediumOptions} pressed={this.state.lesson_medium} onClick={this.onLessonMediumChange} />
          </div>
          : null
        }
        {this.lessonMedium() === 'local' ?
          <React.Fragment>
            <div className="grid gap-2">
              <Label>Address<RequiredHint/></Label>
              {this.props.google_map_loaded ?
                <AddressAutocomplete onPlaceChanged={this.onPlaceChanged}/>
                :
                <Input type="text" placeholder="Loading Google Maps..." disabled />
              }
              {this.state.address ?
                <span className="text-sm text-muted-foreground">{this.state.address.formatted_address}</span>
                : null
              }
            </div>
            <div className="grid gap-2">
              <Label htmlFor="new_lead_flat_unit_estate">Flat/Unit/Estate</Label>
              <Input
                id="new_lead_flat_unit_estate"
                placeholder="e.g. Unit 2"
                value={this.state.flat_unit_estate}
                onChange={(e) => this.setState({flat_unit_estate: e.target.value})}
              />
            </div>
          </React.Fragment>
          : null
        }
      </React.Fragment>
    )
  }

  render() {
    const { countries, saving } = this.props.new_lead
    const country = this.country()
    const budget_options = country ? country.budget_options : []

    return (
      <DockedPanel
        title="New Lead"
        subtitle="For a family who contacted us directly. It becomes an enquiry straight away."
        footer={
          <Button size="sm" disabled={saving} onClick={this.onCreate}>
            {saving ? 'Creating…' : 'Create Lead'}
          </Button>
        }
        onClose={this.onClose}
      >
        <div className="grid gap-6 p-6 text-left">
          <div className="grid gap-2">
            <Label htmlFor="new_lead_country">Country<RequiredHint/></Label>
            <Select value={this.state.country_code || undefined} onValueChange={this.onCountryChange}>
              <SelectTrigger id="new_lead_country" className="w-full">
                <SelectValue placeholder="Select a country…"/>
              </SelectTrigger>
              <SelectContent>
                {_.map(countries, (country) => (
                  <SelectItem key={country.country_code} value={country.country_code}>{country.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-4">
            <span className={SECTION_CLASSES}>Enquirer</span>
            <div className="grid gap-2">
              <Label htmlFor="new_lead_enquirer_name">Name<RequiredHint/></Label>
              <Input
                id="new_lead_enquirer_name"
                value={this.state.enquirer_name}
                onChange={(e) => this.setState({enquirer_name: e.target.value})}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="new_lead_enquirer_email_address">Email Address<RequiredHint/></Label>
              <Input
                id="new_lead_enquirer_email_address"
                type="email"
                value={this.state.enquirer_email_address}
                onChange={(e) => this.setState({enquirer_email_address: e.target.value}, this.lookupMatchesLater)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="new_lead_enquirer_contact_number">Mobile Number<RequiredHint/></Label>
              <Input
                id="new_lead_enquirer_contact_number"
                type="tel"
                value={this.state.enquirer_contact_number}
                onChange={(e) => this.setState({enquirer_contact_number: e.target.value}, this.lookupMatchesLater)}
              />
            </div>
            {this.renderCustomerMatch()}
          </div>

          <div className="grid gap-4">
            <span className={SECTION_CLASSES}>Learner</span>
            <div className="grid gap-2">
              <Label>Learner Type<RequiredHint/></Label>
              <ToggleOptions options={learnerTypeOptions} pressed={this.state.learner_type} onClick={(value) => this.setState({learner_type: value})} />
            </div>
            {this.state.learner_type !== 'myself' ? this.renderLearner() : null}
          </div>

          <div className="grid gap-4">
            <span className={SECTION_CLASSES}>Lessons</span>
            {this.renderLessons()}
          </div>

          <div className="grid gap-4">
            <span className={SECTION_CLASSES}>Other</span>
            {budget_options.length > 0 ?
              <div className="grid gap-2">
                <Label>Budget</Label>
                <ToggleOptions options={budget_options} pressed={this.state.budget} onClick={(value) => this.setState({budget: value})} />
              </div>
              : null
            }
            <div className="grid gap-2">
              <Label>Urgency</Label>
              <ToggleOptions options={urgencyOptions} pressed={this.state.urgency} onClick={(value) => this.setState({urgency: value})} />
            </div>
          </div>
        </div>
      </DockedPanel>
    )
  }
}

export default NewLead
